import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MoopShellProvider, useMoopShell } from './MoopShellContext';
import MoopSider from './MoopSider';
import MoopHeader from './MoopHeader';
import { OnboardingTourProvider } from '../onboarding-tour';
import OnboardingTourRoot from '../onboarding-tour/OnboardingTourRoot';
import styles from './MoopLayout.module.scss';

// Two distinct concepts:
//   • BYPASS_ROUTES   — auth + legacy pages that shouldn't get MOOP styling
//                       at all (no .moop-shell wrapper, no nav, no sider).
//   • CHROMELESS_ROUTES — MOOP pages that need full MOOP styling
//                       (.moop-shell scope for moop-antd-overrides) but
//                       suppress the visible nav + sider. Wizards live here:
//                       Stepper + sticky-footer are the chrome per prototype.
const BYPASS_ROUTES = ['/signin', '/register', '/reset-password', '/settings', '/accept-invite', '/magic-link'];
const CHROMELESS_ROUTES = ['/create'];

interface MoopLayoutProps {
  children: React.ReactNode;
}

const MoopLayoutInner: React.FC<MoopLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { sidebarCollapsed } = useMoopShell();
  const isBypassPage = BYPASS_ROUTES.some((route) => location.pathname.startsWith(route));
  const isChromeless = CHROMELESS_ROUTES.some((route) => location.pathname.startsWith(route));

  useEffect(() => {
    document.body.classList.add('moop-domain');
    return () => {
      document.body.classList.remove('moop-domain');
    };
  }, []);

  if (isBypassPage) {
    return <>{children}</>;
  }

  // Chromeless pages keep the .moop-shell class AND the sider (so global
  // navigation context stays intact), but suppress the top MoopHeader. The
  // page itself owns its top chrome (Stepper + sticky footer). Per the
  // wizard prototype + designer call: top bar with avatar shouldn't compete
  // with the wizard's own Stepper title.
  //
  // `.content` carries a 64px `margin-top: var(--sbds-header-height)` to slot
  // under the fixed MoopHeader; without the header, that margin shows up as
  // an empty grey strip above the page body — strip it on chromeless routes.
  return (
    <div className="moop-shell">
      <MoopSider />
      <div
        className={styles.main}
        style={{
          marginLeft: sidebarCollapsed ? 'var(--sbds-sidebar-width-collapsed)' : 'var(--sbds-sidebar-width-expanded)',
        }}
      >
        {!isChromeless && <MoopHeader />}
        <main className={styles.content} style={isChromeless ? { marginTop: 0 } : undefined}>
          {children}
        </main>
      </div>
    </div>
  );
};

const MoopLayout: React.FC<MoopLayoutProps> = ({ children }) => {
  return (
    <MoopShellProvider>
      <OnboardingTourProvider>
        <MoopLayoutInner>{children}</MoopLayoutInner>
        <OnboardingTourRoot />
      </OnboardingTourProvider>
    </MoopShellProvider>
  );
};

export default MoopLayout;
